/**
 * FinanceBreakdownDialog — drill-down modal for the two Finance cards
 * (Invoiced (Actual) and Pipeline (Uninvoiced)).
 *
 * Same UI pattern as `OrderValueBreakdownDialog` but fetches its data set
 * from `/api/finance/overview/breakdown` so the totals reconcile exactly
 * with the Finance KPI cards. Comes with a "Download Excel" button that
 * hits `/api/finance/overview/breakdown.xlsx` — same server function, so
 * on-screen totals and file totals always match.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../lib/apiBase';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { OrderStatusBadge } from './StatusBadge';
import {
    FileText,
    AlertCircle,
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Download,
    Loader2,
} from 'lucide-react';

const API = `${API_BASE}/api`;

const STATE_LABEL = {
    booked: { text: 'Booked', cls: 'bg-emerald-100 text-emerald-800' },
    economic_draft: { text: 'E-conomic Draft', cls: 'bg-blue-100 text-blue-800' },
    local_draft: { text: 'Local Draft', cls: 'bg-slate-100 text-slate-700' },
    none: { text: 'Uninvoiced', cls: 'bg-amber-100 text-amber-800' },
};

const fmt0 = (n) =>
    (n ?? 0).toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const FinanceBreakdownDialog = ({
    open,
    onOpenChange,
    variant, // 'invoiced' | 'pipeline'
    periodType,
    year,
    periodValue,
}) => {
    const navigate = useNavigate();
    const { token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [sort, setSort] = useState({ field: 'eur', dir: 'desc' });
    const [downloading, setDownloading] = useState(false);

    const isInvoiced = variant === 'invoiced';
    const title = isInvoiced
        ? 'Invoiced (Actual) — Order Breakdown'
        : 'Pipeline (Uninvoiced) — Order Breakdown';
    const desc = isInvoiced
        ? 'Orders whose e-conomic invoice was booked inside the selected period. Revenue equals the booked invoice total.'
        : 'Open orders that have no booked invoice yet — includes drafted sales documents. Revenue is the current best estimate from the sales document, invoice lines, or the order pricing.';
    const accentBg = isInvoiced ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200';
    const accentLine = isInvoiced ? 'border-blue-200' : 'border-purple-200';
    const accentIcon = isInvoiced
        ? <FileText className="h-5 w-5 text-blue-600" />
        : <AlertCircle className="h-5 w-5 text-purple-600" />;

    useEffect(() => {
        if (!open) return;
        setError(null);
        setData(null);
        setLoading(true);
        axios
            .get(`${API}/finance/overview/breakdown`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { variant, period_type: periodType, year, period_value: periodValue },
            })
            .then((res) => setData(res.data))
            .catch((e) => setError(e?.response?.data?.detail || e.message || 'Failed to load breakdown'))
            .finally(() => setLoading(false));
    }, [open, variant, periodType, year, periodValue, token]);

    const orders = data?.orders || [];
    const totals = data?.totals || { count: 0 };

    const valueFor = (row) => {
        switch (sort.field) {
            case 'eur': return row.eur;
            case 'costs': return row.costs_eur;
            case 'gross_profit': return row.gross_profit_eur;
            case 'margin': return row.gross_margin_pct;
            case 'customer': return (row.customer_name || '').toLowerCase();
            default: return row.eur;
        }
    };

    const sorted = useMemo(() => {
        const arr = [...orders];
        arr.sort((a, b) => {
            const av = valueFor(a);
            const bv = valueFor(b);
            const aNull = av == null;
            const bNull = bv == null;
            if (aNull && bNull) return 0;
            if (aNull) return 1;
            if (bNull) return -1;
            if (typeof av === 'string') {
                return sort.dir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
            }
            return sort.dir === 'desc' ? (bv - av) : (av - bv);
        });
        return arr;
    }, [orders, sort]);

    const toggleSort = (field) => {
        setSort((prev) => (prev.field === field
            ? { field, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
            : { field, dir: 'desc' }));
    };

    const renderSortIcon = (field) => {
        if (sort.field !== field) return <ArrowUpDown className="inline h-3 w-3 ml-1 text-slate-300" />;
        return sort.dir === 'desc'
            ? <ArrowDown className="inline h-3 w-3 ml-1 text-slate-500" />
            : <ArrowUp className="inline h-3 w-3 ml-1 text-slate-500" />;
    };

    const downloadExcel = async () => {
        try {
            setDownloading(true);
            const res = await axios.get(`${API}/finance/overview/breakdown.xlsx`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { variant, period_type: periodType, year, period_value: periodValue },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            const period = data?.period_label?.replace(/ /g, '_') || 'period';
            link.setAttribute('download', `finance_${variant}_${period}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message || 'Failed to download');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="sm:max-w-6xl max-h-[85vh] flex flex-col"
                data-testid="finance-breakdown-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {accentIcon}
                        {title}
                        {data?.period_label && (
                            <span className="text-sm font-normal text-slate-500 ml-2">· {data.period_label}</span>
                        )}
                    </DialogTitle>
                    <DialogDescription>{desc}</DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-between gap-3 -mt-1 mb-2">
                    <div className="text-xs text-slate-500">
                        Totals reconcile with the Finance card by construction.
                    </div>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={downloadExcel}
                        disabled={downloading || loading || orders.length === 0}
                        data-testid="finance-breakdown-download-xlsx"
                    >
                        {downloading
                            ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                            : <Download className="h-4 w-4 mr-1" />}
                        Download Excel
                    </Button>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center py-16 text-slate-500">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading breakdown…
                    </div>
                ) : error ? (
                    <div className="flex-1 flex items-center justify-center py-10 text-sm text-rose-600">
                        {error}
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto -mx-6 px-6">
                        <div className={`mb-3 grid grid-cols-2 sm:grid-cols-6 gap-3 text-sm ${accentBg} border rounded-lg p-3`}>
                            <div>
                                <div className="text-xs text-slate-500">Orders</div>
                                <div className="text-base font-semibold text-slate-900" data-testid="fb-total-count">
                                    {totals.count ?? 0}
                                </div>
                            </div>
                            <div className={`border-l ${accentLine} pl-3`}>
                                <div className="text-xs text-slate-500">Revenue EUR</div>
                                <div className="text-base font-semibold text-slate-900" data-testid="fb-total-revenue">
                                    {fmt0(totals.revenue_eur)}
                                </div>
                            </div>
                            <div className={`border-l ${accentLine} pl-3`}>
                                <div className="text-xs text-slate-500">Revenue DKK</div>
                                <div className="text-base font-semibold text-slate-900">
                                    {fmt0(totals.revenue_dkk)}
                                </div>
                            </div>
                            <div className={`border-l ${accentLine} pl-3`}>
                                <div className="text-xs text-slate-500">Direct Costs</div>
                                <div className="text-base font-semibold text-slate-900" data-testid="fb-total-costs">
                                    {fmt0(totals.costs_eur)}
                                </div>
                            </div>
                            <div className={`border-l ${accentLine} pl-3`}>
                                <div className="text-xs text-slate-500">Gross Profit</div>
                                <div className="text-base font-semibold text-slate-900" data-testid="fb-total-gross-profit">
                                    {fmt0(totals.gross_profit_eur)}
                                </div>
                            </div>
                            <div className={`border-l ${accentLine} pl-3`}>
                                <div className="text-xs text-slate-500">Gross Margin</div>
                                <div className="text-base font-semibold text-slate-900">
                                    {totals.gross_margin_pct != null ? `${totals.gross_margin_pct.toFixed(1)}%` : '—'}
                                </div>
                            </div>
                        </div>

                        {orders.length === 0 ? (
                            <div className="py-10 text-center text-sm text-slate-500">
                                {isInvoiced
                                    ? 'No booked invoices in this period.'
                                    : 'No open pipeline orders — everything is booked.'}
                            </div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="text-xs text-slate-500 uppercase tracking-wide border-b">
                                    <tr>
                                        <th className="text-left py-2 font-medium">Order</th>
                                        <th
                                            className="text-left py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                            onClick={() => toggleSort('customer')}
                                            data-testid="fb-sort-customer"
                                        >
                                            Customer {renderSortIcon('customer')}
                                        </th>
                                        <th className="text-left py-2 font-medium">Status</th>
                                        <th className="text-left py-2 font-medium">Invoice</th>
                                        <th className="text-right py-2 font-medium">Local</th>
                                        <th
                                            className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                            onClick={() => toggleSort('eur')}
                                            data-testid="fb-sort-eur"
                                        >
                                            EUR {renderSortIcon('eur')}
                                        </th>
                                        <th
                                            className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                            onClick={() => toggleSort('costs')}
                                            data-testid="fb-sort-costs"
                                        >
                                            Costs {renderSortIcon('costs')}
                                        </th>
                                        <th
                                            className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700"
                                            onClick={() => toggleSort('gross_profit')}
                                            data-testid="fb-sort-gp"
                                        >
                                            Gross Profit {renderSortIcon('gross_profit')}
                                        </th>
                                        <th
                                            className="text-right py-2 font-medium cursor-pointer select-none hover:text-slate-700 pl-1"
                                            onClick={() => toggleSort('margin')}
                                            data-testid="fb-sort-margin"
                                        >
                                            % {renderSortIcon('margin')}
                                        </th>
                                        <th className="text-left py-2 font-medium pl-3">E-conomic Invoice #</th>
                                        <th className="text-left py-2 font-medium pl-3">Reason</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {sorted.map((d) => {
                                        const lbl = STATE_LABEL[d.invoice_state] || STATE_LABEL.none;
                                        const marginPct = d.gross_margin_pct;
                                        const profitEur = d.gross_profit_eur;
                                        let marginCls = 'text-slate-400';
                                        if (marginPct != null) {
                                            if (marginPct < 20) marginCls = 'text-rose-600 font-medium';
                                            else if (marginPct < 35) marginCls = 'text-amber-600';
                                            else marginCls = 'text-emerald-700 font-medium';
                                        }
                                        const profitCls = profitEur < 0 ? 'text-rose-600 font-medium' : 'text-slate-900';
                                        return (
                                            <tr
                                                key={d.id}
                                                className="hover:bg-slate-50 cursor-pointer"
                                                onClick={() => { onOpenChange(false); navigate(`/orders/${d.id}`); }}
                                                data-testid={`fb-row-${d.order_number}`}
                                            >
                                                <td className="py-2">
                                                    <div className="font-medium text-slate-900">
                                                        #{d.order_number != null ? String(d.order_number).padStart(6, '0') : d.id?.slice(0, 6)}
                                                    </div>
                                                    <div className="text-xs text-slate-500 truncate max-w-[240px]">
                                                        {d.title}
                                                    </div>
                                                </td>
                                                <td className="py-2 text-slate-700 truncate max-w-[180px]">
                                                    {d.customer_name || '—'}
                                                </td>
                                                <td className="py-2">
                                                    <OrderStatusBadge status={d.status} />
                                                </td>
                                                <td className="py-2">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium ${lbl.cls}`}>
                                                        {lbl.text}
                                                    </span>
                                                </td>
                                                <td className="py-2 text-right tabular-nums text-slate-500">
                                                    {fmt0(d.order_value_local)} {d.order_currency}
                                                </td>
                                                <td className="py-2 text-right tabular-nums font-medium">
                                                    {fmt0(d.eur)}
                                                </td>
                                                <td className="py-2 text-right tabular-nums text-slate-500">
                                                    {fmt0(d.costs_eur)}
                                                </td>
                                                <td className={`py-2 text-right tabular-nums ${profitCls}`}>
                                                    {fmt0(profitEur)}
                                                </td>
                                                <td className={`py-2 text-right tabular-nums pl-1 ${marginPct == null ? 'text-slate-400' : marginCls}`}>
                                                    {marginPct != null ? `${marginPct.toFixed(1)}%` : '—'}
                                                </td>
                                                <td className="py-2 pl-3 text-xs text-slate-600 tabular-nums" data-testid={`fb-econ-invoice-${d.order_number}`}>
                                                    {d.economic_invoice_number || '—'}
                                                </td>
                                                <td className="py-2 pl-3 text-xs text-slate-500 max-w-[240px]">
                                                    {d.inclusion_reason}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default FinanceBreakdownDialog;
